import { Box, Card, Flex, Text } from '@mantine/core';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, TooltipItem } from 'chart.js';
import theme from 'theme';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import { xScaleConfig, yGridConfig, barDatasetBase, formatEur4 } from 'pages/tuya-device/components/chartHelpers';
import useLang from 'lang';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface FixedTariffChartCardProps {
  /** Cost per interval at the fixed tariff (€), null for intervals with no consumption */
  fixedTariffCostData: (number | null)[];
  /** Labels for valid (non-null consumption) intervals */
  validIntervalLabels: (string | undefined)[];
  /** Total cost at the fixed tariff for the day */
  totalFixedTariffCost: number | null;
  /** Total cost at spot prices for comparison (may be null if no spot data) */
  totalCost: number | null;
  /** Fixed tariff in €/MWh used for the calculation */
  fixedTariff: number;
  /** The date string from the API response */
  date: string;
  /** Shared Y-axis max with the spot cost chart */
  sharedCostYMax: number | undefined;
}

/**
 * Displays a bar chart of electricity costs calculated at a fixed tariff.
 *
 * @prop {(number | null)[]} fixedTariffCostData - Per-interval fixed costs in €.
 * @prop {(string | undefined)[]} validIntervalLabels - X-axis labels.
 * @prop {number | null} totalFixedTariffCost - Total fixed cost in €.
 * @prop {number | null} totalCost - Total spot cost for comparison display.
 * @prop {number} fixedTariff - The fixed tariff rate in €/MWh.
 * @prop {string} date - ISO date string for the chart title.
 * @prop {number | undefined} sharedCostYMax - Shared Y maximum with the SPOT chart.
 */
export default function FixedTariffChartCard({
  fixedTariffCostData,
  validIntervalLabels,
  totalFixedTariffCost,
  totalCost,
  fixedTariff,
  date,
  sharedCostYMax,
}: FixedTariffChartCardProps) {
  const { _t } = useLang();
  const chartData = useMemo(
    () => ({
      labels: validIntervalLabels,
      datasets: [
        {
          ...barDatasetBase,
          label: _t('Price (€)'),
          data: fixedTariffCostData.filter((c) => c !== null),
          backgroundColor: '#3B82F6',
          hoverBackgroundColor: '#2563EB',
        },
      ],
    }),
    [_t, validIntervalLabels, fixedTariffCostData]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { ...xScaleConfig },
        y: {
          ticks: { color: theme.colors?.gray?.[7], callback: formatEur4 },
          grid: yGridConfig,
          beginAtZero: true,
          max: sharedCostYMax,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: { label: (ctx: TooltipItem<'bar'>) => `${_t('Price')}: ${ctx.parsed.y.toFixed(6)} €` },
        },
      },
    }),
    [_t, sharedCostYMax]
  );

  const difference = totalCost !== null && totalFixedTariffCost !== null ? totalFixedTariffCost - totalCost : null;

  return (
    <Card p={20} radius={10} shadow="sm" mt={20}>
      <H2SemiBold mb={20}>
        {_t('Price at fixed tariff')} - {new Date(date).toLocaleDateString('sk-SK')}
      </H2SemiBold>
      <Box style={{ height: 400 }}>
        <Bar data={chartData} options={chartOptions} />
      </Box>

      <Box mt={20} p={15} style={{ backgroundColor: '#EFF6FF', borderRadius: 8, border: '1px solid #DBEAFE' }}>
        <Flex justify="space-between" align="center">
          <Text size="lg" fw={600} c="gray.7">
            {_t('Total daily price (fixed tariff)')}:
          </Text>
          <Text size="xl" fw={700} c="#3B82F6">
            {totalFixedTariffCost !== null ? totalFixedTariffCost.toFixed(4) : '0.0000'} €
          </Text>
        </Flex>
        <Text size="sm" c="gray.6" mt={5}>
          {_t('Fixed tariff')}: {fixedTariff.toFixed(2)} €/MWh
        </Text>
        {difference !== null && (
          <Text size="sm" c="gray.6" mt={5}>
            {_t('Difference compared to spot')}: {difference.toFixed(4)} € (
            {difference > 0 ? _t('more expensive') : _t('cheaper')})
          </Text>
        )}
      </Box>
    </Card>
  );
}
